import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "react-router-dom";
import { SupportModal } from "@/components/support-modal";
import { AdvisorContactModal } from "@/components/advisor-contact-modal";
import { 
  User, 
  Calendar, 
  MessageSquare, 
  Settings, 
  LogOut,
  Phone,
  Mail,
  Building,
  Clock,
  CheckCircle
} from "lucide-react";

export default function Portal() {
  const { user, userProfile, isAdmin, signOut } = useAuth();
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [advisorModalOpen, setAdvisorModalOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-primary">Portal Cliente</h1>
              <Link to="/">
                <Badge 
                  variant="outline" 
                  className="hover:bg-primary hover:text-primary-foreground transition-colors cursor-pointer"
                >
                  MKTN GUARDIANS
                </Badge>
              </Link>
            </div>
            <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 sm:gap-4">
              <div className="flex items-center space-x-2 text-xs sm:text-sm text-muted-foreground order-2 sm:order-1">
                <User className="h-4 w-4" />
                <span className="truncate max-w-[120px] sm:max-w-none">{userProfile?.full_name || 'Usuario'}</span>
              </div>
              <div className="flex items-center gap-2 order-1 sm:order-2">
                {isAdmin && (
                  <Link to="/admin">
                    <Button variant="outline" size="sm" className="text-xs sm:text-sm">
                      Admin
                    </Button>
                  </Link>
                )}
                <Button variant="outline" size="sm" onClick={handleSignOut} className="text-xs sm:text-sm">
                  <LogOut className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Cerrar Sesión</span>
                  <span className="sm:hidden">Salir</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-4 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
          {/* Welcome Section */}
          <div className="lg:col-span-3">
            <Card className="gradient-primary text-white">
              <CardContent className="p-4 sm:p-8">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-center sm:text-left">
                    <h2 className="text-xl sm:text-3xl font-bold mb-2">
                      ¡Bienvenido{userProfile?.full_name ? `, ${userProfile.full_name}` : ''}!
                    </h2>
                    <p className="text-white/90 text-sm sm:text-lg">
                      Gestiona tu cuenta y servicios de marketing digital
                    </p>
                  </div>
                  <User className="h-12 w-12 sm:h-16 sm:w-16 text-white/80" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Account Information */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center text-base sm:text-lg">
                <User className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                Información de Cuenta
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                {userProfile && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-muted-foreground">Nombre Completo</label>
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm sm:text-base">{userProfile.full_name}</span>
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Email</label>
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm sm:text-base break-all">{user?.email}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Estado</label>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-green-600 text-sm sm:text-base">Activo</span>
                  </div>
                </div>
              </div>
              
              <Separator />
              
              <div className="space-y-4">
                <h3 className="font-semibold text-sm sm:text-base">Servicios Contratados</h3>
                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-sm">No tienes servicios contratados actualmente</p>
                  <p className="text-xs mt-1">Contacta con tu asesor para más información</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <div className="space-y-4 sm:space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-base sm:text-lg">
                  <Settings className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  Acciones Rápidas
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Link to="/reservar-cita">
                  <Button className="w-full justify-start gradient-gold text-sm sm:text-base">
                    <Calendar className="h-4 w-4 mr-2" />
                    Agendar Nueva Cita
                  </Button>
                </Link>
                <Button 
                  variant="outline" 
                  className="w-full justify-start text-sm sm:text-base"
                  onClick={() => setSupportModalOpen(true)}
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Soporte Técnico
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start text-sm sm:text-base"
                  onClick={() => setAdvisorModalOpen(true)}
                >
                  <Phone className="h-4 w-4 mr-2" />
                  Contactar Asesor
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base sm:text-lg">Estado del Servicio</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs sm:text-sm">Campañas Activas</span>
                    <Badge variant="outline" className="text-xs">0</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs sm:text-sm">Leads Generados</span>
                    <Badge variant="outline" className="text-xs">0</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs sm:text-sm">Conversiones</span>
                    <Badge variant="outline" className="text-xs">0</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Activity */}
          <div className="lg:col-span-3">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-base sm:text-lg">
                  <Clock className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  Actividad Reciente
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8 text-muted-foreground">
                  <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-sm">No hay actividad reciente</p>
                  <p className="text-xs mt-1">La actividad aparecerá aquí cuando tengas servicios activos</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <SupportModal 
        open={supportModalOpen} 
        onOpenChange={setSupportModalOpen} 
      />
      
      <AdvisorContactModal 
        open={advisorModalOpen} 
        onOpenChange={setAdvisorModalOpen} 
      />
    </div>
  );
}